import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { useFormIDProxy } from '@chancenwerk/client/form-id-proxy.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { Partner } from '@chancenwerk/shared/master-data-management/partner/entities/Partner.js';
import { subscribeWorkshopAttendanceSimple } from '@chancenwerk/shared/workshops/api/subscribeWorkshopAttendanceSimple.js';
import { subscribeWorkshopTrainingAttendanceSimple } from '@chancenwerk/shared/workshops/api/subscribeWorkshopTrainingAttendanceSimple.js';
import { Workshop } from '@chancenwerk/shared/workshops/entities/Workshop.js';
import { WorkshopAttendance } from '@chancenwerk/shared/workshops/entities/WorkshopAttendance.js';
import { WorkshopFilter } from '@chancenwerk/shared/workshops/entities/WorkshopFilter.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/shared/workshops/entities/WorkshopTrainingAttendance.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { type Observable, of, switchMap } from 'rxjs';

/**
 * @customElement kraefte-component-workshop-attendance
 */
@customElement('kraefte-component-workshop-attendance')
export class KraefteComponentWorkshopAttendance extends ChemistryLitElement {
  @property({ attribute: false }) userID = new RXController<Maybe<string>>(this, null);

  @state() _partnerProxy = useFormIDProxy<Partner>(
    (id: string) => {
      if (!id || id === '') {
        return null;
      }

      return Partner.getByID<Partner>(id)({ _id: 1, name: 1, kuerzel: 1 });
    },
    () => this.requestUpdate(),
  );

  @state() _workshops = new RXController(
    this,
    subscribeAllEntities(Workshop)({ _id: 1, title: 1, roles: 1, requiresTraining: 1 }).pipe(
      switchMap(async w => {
        return w.filter(workshop => {
          return (
            workshop.title?.toLowerCase().includes('Einführung Lernkaskade'.toLowerCase()) ||
            workshop.title?.toLowerCase().includes('CHANCENspirit'.toLowerCase())
          );
        });
      }),
    ),
  );

  @state() _attendance = new RXController<Observable<WorkshopAttendance[]>>(
    this,
    this.userID.observable.pipe(
      switchMap(id => {
        if (!id) {
          return of([]);
        }

        return subscribeWorkshopAttendanceSimple(
          0,
          1000,
          new WorkshopFilter({ userID: id }),
        )(WorkshopAttendance.allFields);
      }),
    ),
  );

  @state() _trainingAttendance = new RXController<Observable<WorkshopTrainingAttendance[]>>(
    this,
    this.userID.observable.pipe(
      switchMap(id => {
        if (!id) {
          return of([]);
        }

        return subscribeWorkshopTrainingAttendanceSimple(
          0,
          1000,
          new WorkshopFilter({ userID: id }),
        )(WorkshopTrainingAttendance.allFields);
      }),
    ),
  );

  public static selectionSets() {
    return { WorkshopAttendance: WorkshopAttendance.allFields };
  }

  private _renderAttendance(workshop: Workshop, i: number) {
    const attendance = this._attendance.value?.[i];
    if (!attendance) {
      return;
    }

    return html`<cr-grid
      columns=${workshop.requiresTraining ? '0.5fr 1fr 0.5fr' : '1fr 1fr'}
      crossaxis-center
      space="md"
    >
      <cr-text>${attendance.date ? attendance.date.toFormat('dd.MM.yyyy') : 'Kein Datum'}</cr-text>
      ${!workshop.requiresTraining
        ? nothing
        : html`<cr-text>${this._partnerProxy.value(attendance.partnerID ?? '')?.name ?? 'Kein Partner'}</cr-text>
            <cr-text>${attendance.attendanceCount ?? 'Keine Anzahl'}</cr-text>`}
      ${workshop.requiresTraining ? nothing : html`<cr-text>${attendance.state ?? 'Kein Status'}</cr-text>`}
    </cr-grid>`;
  }

  private _renderTrainingAttendance(workshop: Workshop) {
    if (!workshop.requiresTraining) {
      return nothing;
    }

    const attendance = this._trainingAttendance.value?.filter(a => a.workshopID === workshop._id) ?? [];
    if (attendance.length === 0) {
      return html` <cr-flex space="md">
        <cr-text bold>Schulung erhalten</cr-text>
        <cr-text ${css({ opacity: 0.7 })}>Noch keine hinterlegt...</cr-text>
      </cr-flex>`;
    }

    return html` <cr-flex space="md">
      <cr-text bold>Schulung erhalten</cr-text>
      <cr-grid columns="1fr 1fr" space="md">
        ${repeat(
          attendance,
          a => a._id,
          a =>
            html` <cr-text>${a.date ? a.date.toFormat('dd.MM.yyyy') : 'Kein Datum'}</cr-text>
              <cr-text>${a.state ?? 'Kein Status'}</cr-text>`,
        )}
      </cr-grid>
    </cr-flex>`;
  }

  override render() {
    if (!this.userID.value) {
      return html`<cr-text>Workshops können erst nach dem Speichern erfasst werden.</cr-text>`;
    }

    if ((this._workshops.value ?? []).length === 0) {
      return html`<cr-flex
        ${css({ opacity: 0.5, padding: `${this.spacing.sm} ${this.spacing.sm} 0 ${this.spacing.sm}` })}
      >
        <cr-text>Es sind noch keine Workshops angelegt.</cr-text>
      </cr-flex>`;
    }

    return html`<cr-grid columns="1fr" space="md">
      ${repeat(
        this._workshops.value ?? [],
        w => w._id,
        w => {
          const attendanceIndices = (this._attendance.value ?? [])
            .map((a, i) => (a.workshopID === w._id ? i : null))
            .filter(i => i !== null) as number[];

          return html`<cr-flex>
            <cr-flex
              padding="md"
              space-between
              horizontal
              crossaxis-center
              ${css({
                paddingLeft: this.spacing.sm,
                background: this.colors.neutral.shade(100),
                borderRadius: `${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary} 0 0`,
              })}
            >
              <cr-text> ${w.title} </cr-text>
            </cr-flex>
            <cr-grid
              columns="${w.requiresTraining ? '400px 1fr' : '1fr'}"
              padding="md"
              space="md"
              ${css({
                border: `1px solid ${this.colors.neutral.shade(100)}`,
                borderRadius: `0 0 ${this.sizes.borderRadiusSecondary} ${this.sizes.borderRadiusSecondary}`,
              })}
            >
              ${this._renderTrainingAttendance(w)}

              <cr-flex
                space="md"
                ${css(
                  w.requiresTraining ? { borderLeft: '1px solid rgba(0,0,0,0.1)', paddingLeft: this.spacing.md } : {},
                )}
              >
                <cr-text bold>${w.requiresTraining ? 'Workshops durchgeführt' : 'Teilnahmen'}</cr-text>
                ${attendanceIndices.length === 0
                  ? html`<cr-text ${css({ opacity: 0.7 })}>Noch keine hinterlegt...</cr-text>`
                  : nothing}
                ${attendanceIndices.map(i => this._renderAttendance(w, i))}
              </cr-flex>
            </cr-grid>
          </cr-flex>`;
        },
      )}
    </cr-grid>`;
  }
}
